import { useState } from "react";
import { AlertOctagon, CheckCircle2, Loader2, Search, XCircle } from "lucide-react";
import { api, dt, errMsg, money, PAY_LABEL } from "@/lib/api";
import QrScanner from "@/components/QrScanner";
import { ErrorBox, PermitBadge } from "@/components/common";
import { SelectionPhotos } from "@/components/Selection";

const RESULT_STYLE = { valid: ["bg-emerald-600", CheckCircle2], not_found: ["bg-rose-700", XCircle], cancelled: ["bg-rose-700", XCircle],
  expired: ["bg-rose-700", XCircle], used: ["bg-stone-700", AlertOctagon], exited: ["bg-stone-700", AlertOctagon], mismatch: ["bg-rose-700", XCircle], sale_cancelled: ["bg-rose-700", XCircle] };

export function useVerifier() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const verify = async (code, ear_tag) => {
    setBusy(true); setErr(""); setRes(null);
    try { const { data } = await api.post("/permits/verify", { code, ear_tag: ear_tag || null }); setRes(data); return data; }
    catch (e) { setErr(errMsg(e)); return null; } finally { setBusy(false); }
  };
  return { res, setRes, busy, err, verify };
}

export function VerifyInput({ v }) {
  const [code, setCode] = useState("");
  const [tag, setTag] = useState("");
  return (
    <div className="k-card p-5 space-y-4">
      <QrScanner onScan={(t) => { setCode(t); v.verify(t, tag); }} />
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (code.trim()) v.verify(code.trim(), tag); }}>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="k-input h-14 text-lg num text-center" placeholder="رقم التصريح مثل X-2026-0001" value={code} onChange={(e) => setCode(e.target.value)} data-testid="permit-code-input" />
          <input className="k-input h-14 text-lg num text-center" placeholder="رقم الكبش (للمطابقة – اختياري)" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="permit-eartag-check-input" />
        </div>
        <button className="k-btn-primary w-full h-14 text-lg" disabled={v.busy || !code.trim()} data-testid="permit-verify-button">
          {v.busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} تحقق
        </button>
      </form>
      <ErrorBox text={v.err} />
    </div>
  );
}

export function VerifyResult({ res, children }) {
  if (!res) return null;
  const [bg, Icon] = RESULT_STYLE[res.result] || RESULT_STYLE.not_found;
  const p = res.permit;
  return (
    <div className="k-card overflow-hidden fade-up" data-testid="verify-result">
      <div className={`${bg} text-white p-5 flex items-center gap-3`} data-testid={`verify-result-${res.result}`}>
        <Icon className="w-10 h-10 shrink-0" />
        <div className="text-xl sm:text-2xl font-extrabold font-display" data-testid="verify-result-message">{res.message}</div>
      </div>
      {p && (
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div><div className="text-xs text-slate-500">رقم الكبش</div><div className="text-5xl font-black num text-[#1E40AF]" data-testid="verify-ear-tag">{p.ear_tag}</div></div>
            <PermitBadge p={p} testid="verify-permit-badge" />
          </div>
          {p.selection_id && <div className="rounded-xl border-2 border-[#1E40AF]/30 bg-blue-50/50 p-3" data-testid="verify-photos"><div className="text-sm font-bold text-[#1E40AF] mb-2">صور الكبش – طابقها مع الكبش الخارج</div><SelectionPhotos sel={{ id: p.selection_id, photos: p.photos }} /></div>}
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-slate-500">رقم التصريح</dt><dd className="font-bold num">{p.number}</dd>
            <dt className="text-slate-500">الزبون</dt><dd className="font-bold">{p.customer_name} <span className="num text-slate-500">{p.customer_phone}</span></dd>
            <dt className="text-slate-500">القسم</dt><dd>{p.section_name}</dd>
            <dt className="text-slate-500">حالة الدفع</dt><dd>{PAY_LABEL[p.payment_status]}{p.remaining != null && ` – المتبقي ${money(p.remaining)}`}</dd>
            <dt className="text-slate-500">صدر في</dt><dd>{dt(p.issued_at)} – {p.issued_by}</dd>
            {p.expires_at && <><dt className="text-slate-500">صالح إلى</dt><dd>{dt(p.expires_at)}</dd></>}
            <dt className="text-slate-500">التسليم في الحظيرة</dt><dd className="font-bold">{p.delivered_at ? `${dt(p.delivered_at)} – ${p.delivered_by}` : "لم يُسلّم بعد"}</dd>
            <dt className="text-slate-500">الخروج</dt><dd className="font-bold">{p.exited_at ? `${dt(p.exited_at)} – ${p.exited_by}` : "لم يخرج"}</dd>
            {p.exception && <><dt className="text-rose-700">ملاحظة</dt><dd className="text-rose-700 font-bold">صدر باستثناء معتمد من المدير</dd></>}
            {p.extra_conditions && <><dt className="text-slate-500">تعليمات</dt><dd>{p.extra_conditions}</dd></>}
          </dl>
          {children}
        </div>
      )}
    </div>
  );
}
